/* eslint-disable @typescript-eslint/no-explicit-any */
import { StyledCard } from '@app/components/card/Card';
import { DateRangeSelect } from '@app/components/date-range-select/DateRangeSelect';
import InteractionsLineChart from '@app/components/interactions-line-chart/InteractionsLineChart';
import { PageMeta } from '@app/components/meta/Meta';
import { PageLayout } from '@app/components/page';
import { PageHeader } from '@app/components/page/PageHeader';
import { PageIntro } from '@app/components/page/PageIntro';
import { PageTitle } from '@app/components/page/PageTitle';
import { StyledTag } from '@app/components/tag/Tag';
import useMe from '@app/hooks/auth/useMe';
import useDashboardStats from '@app/hooks/dashboard/useDashboardStats';
import { fn } from '@app/utils';
import { interactionTypeColours } from '@app/utils/constants';
import { Badge, Flex, List, Skeleton, Statistic } from 'antd';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { useMemo, useState } from 'react';
import { BiTrendingDown, BiTrendingUp } from 'react-icons/bi';

const FormattedTimeStat = ({ value }: { value: number | string }) => {
    return (
        <>
            {value} <span style={{ fontSize: 20 }}>mins</span>
        </>
    );
};

export const Dashboard = () => {
    const { useGroupStats, useDailyStats, useGrouped, useGroupedByBrand, useGroupedByLocation } =
        useDashboardStats();
    useMe();

    const [params, setParams] = useState({
        start: '',
        end: '',
    });

    const { data: statsData, isLoading: statsLoading } = useDailyStats(params, {
        staleTime: 1000 * 60 * 5,
    });

    const { data: groupData, isLoading: groupLoading } = useGroupStats(params, {
        staleTime: 1000 * 60 * 5,
    });

    const { data: groupedData, isLoading: groupedLoading } = useGrouped(params, {
        staleTime: 1000 * 60 * 5,
    });

    const { data: groupedByBrandData, isLoading: groupedByBrandLoading } = useGroupedByBrand(
        params,
        {
            staleTime: 1000 * 60 * 5,
        }
    );

    const { data: groupedByLocationData, isLoading: groupedByLocationLoading } =
        useGroupedByLocation(params, {
            staleTime: 1000 * 60 * 5,
        });

    const renderTrending = (value: number, direction: 'up' | 'down') => {
        const DirectionIcon = direction === 'up' ? BiTrendingUp : BiTrendingDown;
        return (
            <Statistic
                value={`${value}%`}
                prefix={<DirectionIcon size={14} />}
                className={`trending-badge direction-${direction}`}
            />
        );
    };

    const groupDataMap = useMemo(() => {
        const map: Record<
            string,
            { current_minutes: number; current_total: number; difference: number }
        > = {};
        if (!Array.isArray(groupData)) return map;

        groupData.forEach((item: any) => {
            const key = String(item.type ?? '')
                .toLowerCase()
                .replace(/[^a-z]/g, '');
            map[key] = {
                current_minutes:
                    typeof item.current_minutes === 'number' ? round(item.current_minutes, 2) : 0,
                current_total:
                    typeof item.current_total === 'number' ? round(item.current_total, 2) : 0,
                difference: typeof item.difference === 'number' ? round(item.difference, 2) : 0,
            };
        });

        return map;
    }, [groupData]);

    const getTrendingSuffix = (difference?: number | null) => {
        if (difference == null) return null;
        const formattedValue = Number(difference.toFixed(2));
        if (difference > 0) return renderTrending(formattedValue, 'up');
        if (difference < 0) return renderTrending(Math.abs(formattedValue), 'down');
        return null;
    };

    const getDifferenceInDays = (date1: string, date2: string) => {
        if (!date1 || !date2) {
            return 0;
        }

        const start = dayjs(date1);
        const end = dayjs(date2);
        const differenceInDays = end.diff(start, 'days') + 1; // offset 1 day
        return differenceInDays;
    };

    const [now] = useState(() => Date.now());
    const timeSeriesData = useMemo(() => {
        const differenceInDays = getDifferenceInDays(params.start, params.end);
        const startDate = params.end ? new Date(params.end).getTime() : now;
        const items = Array.isArray(statsData) ? statsData : [];
        return fn.generateTimeSeriesData(differenceInDays, items, startDate);
    }, [params, statsData, now]);

    return (
        <>
            <PageMeta title="Dashboard" />

            <PageHeader>
                <Flex justify="space-between" gap={20}>
                    <div>
                        <PageTitle title="Dashboard" />
                        <PageIntro>
                            <p>Overview of your interactions and performance metrics</p>
                        </PageIntro>
                        {/* <div
                            style={{
                                marginTop: 6,
                                color: 'var(--muted-color)',
                            }}
                        >
                            <small>
                                Showing: <strong>{startDate}</strong> —{' '}
                                <strong>{endDate}</strong>
                            </small>
                        </div> */}
                    </div>

                    <DateRangeSelect
                        disabled={statsLoading || groupLoading}
                        onSelectionChange={(value) => setParams(value)}
                    />
                </Flex>
            </PageHeader>

            <PageLayout>
                <PageLayout.Content style={{ overflow: 'initial' }}>
                    <div className="grid">
                        <div className="grid-xs-12 grid-s-6 grid-md-2">
                            <StyledCard title="Total Interactions">
                                <Statistic
                                    value={groupDataMap['total']?.current_total ?? 0}
                                    suffix={getTrendingSuffix(groupDataMap['total']?.difference)}
                                    className="statistic-with-trending"
                                />
                            </StyledCard>
                        </div>

                        <div className="grid-xs-12 grid-s-6 grid-md-2">
                            <StyledCard title="Total Calls">
                                <Statistic
                                    value={groupDataMap['call']?.current_total ?? 0}
                                    suffix={getTrendingSuffix(groupDataMap['call']?.difference)}
                                    className="statistic-with-trending"
                                />
                            </StyledCard>
                        </div>

                        <div className="grid-xs-12 grid-s-6 grid-md-2">
                            <StyledCard title="Total Call Time">
                                <Statistic
                                    value={groupDataMap['call']?.current_minutes ?? 0}
                                    formatter={(value) => <FormattedTimeStat value={value} />}
                                    className="statistic-with-trending"
                                />
                            </StyledCard>
                        </div>

                        <div className="grid-xs-12 grid-s-6 grid-md-2">
                            <StyledCard title="Total WhatsApp">
                                <Statistic
                                    value={groupDataMap['whatsapp']?.current_total ?? 0}
                                    suffix={getTrendingSuffix(groupDataMap['whatsapp']?.difference)}
                                    className="statistic-with-trending"
                                />
                            </StyledCard>
                        </div>

                        <div className="grid-xs-12 grid-s-6 grid-md-2">
                            <Badge.Ribbon text="BETA" color="#1b335a">
                                <StyledCard title="Total Emails">
                                    <Statistic
                                        value={groupDataMap['email']?.current_total ?? 0}
                                        suffix={getTrendingSuffix(
                                            groupDataMap['email']?.difference
                                        )}
                                        className="statistic-with-trending"
                                    />
                                </StyledCard>
                            </Badge.Ribbon>
                        </div>

                        <div className="grid-xs-12 grid-s-6 grid-md-2">
                            <Badge.Ribbon text="BETA" color="#1b335a">
                                <StyledCard title="Total Live Chat">
                                    <Statistic
                                        value={groupDataMap['livechat']?.current_total ?? 0}
                                        suffix={getTrendingSuffix(
                                            groupDataMap['livechat']?.difference
                                        )}
                                        className="statistic-with-trending"
                                    />
                                </StyledCard>
                            </Badge.Ribbon>
                        </div>
                    </div>

                    <br />

                    <div className="grid">
                        <Statistic
                            title="Interactions Over Time"
                            styles={{ content: { display: 'none' } }}
                            className="grid-xs-12"
                        />
                        <StyledCard className="grid-xs-12">
                            <InteractionsLineChart
                                data={timeSeriesData}
                                series={[
                                    {
                                        key: 'total',
                                        name: 'Total Interactions',
                                        color: interactionTypeColours['total'],
                                    },
                                    {
                                        key: 'calls',
                                        name: 'Calls',
                                        color: interactionTypeColours['call'],
                                    },
                                    {
                                        key: 'emails',
                                        name: 'Emails',
                                        color: interactionTypeColours['email'],
                                    },
                                    {
                                        key: 'whatsapp',
                                        name: 'WhatsApp',
                                        color: interactionTypeColours['whatsapp'],
                                    },
                                    {
                                        key: 'liveChat',
                                        name: 'Live Chat',
                                        color: interactionTypeColours['livechat'],
                                    },
                                ]}
                            />
                        </StyledCard>
                    </div>

                    <br />

                    <div className="grid">
                        {Array.isArray(groupedData) &&
                            groupedData.map((group: any) => {
                                const typeName = group.type ?? 'Unknown';

                                const cardTitle = (() => {
                                    const t = String(typeName).toLowerCase();
                                    if (t === 'sales') return 'Sales Viewings Booked';
                                    if (t === 'lettings') return 'Lettings Viewings Booked';
                                    if (t === 'valuations') return 'Valuations Booked';
                                    return String(typeName)
                                        .split(/[\s-_]+/)
                                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                        .join(' ');
                                })();

                                return (
                                    <StyledCard
                                        key={String(typeName)}
                                        className="grid-xs-12 grid-s-6 grid-md-2"
                                        title={cardTitle}>
                                        <Skeleton active loading={groupedLoading} paragraph={false}>
                                            <Statistic
                                                value={group?.current_total}
                                                className="statistic-with-trending"
                                            />
                                        </Skeleton>
                                    </StyledCard>
                                );
                            })}
                    </div>

                    <br />

                    <div className="grid">
                        <Statistic
                            title="Performance by Brand"
                            styles={{ content: { display: 'none' } }}
                            className="grid-xs-12"
                        />

                        {Array.isArray(groupedByBrandData) &&
                            groupedByBrandData.map((group: any) => {
                                const typeName = group.type ?? 'Unknown';
                                const items: Array<{
                                    label?: string;
                                    total?: number;
                                }> = Array.isArray(group.items) ? group.items : [];

                                const sorted = items
                                    .slice()
                                    .sort((a, b) => (b.total ?? 0) - (a.total ?? 0));

                                const cardTitle = (() => {
                                    const t = String(typeName).toLowerCase();
                                    if (t === 'total') return 'Total interactions';
                                    if (t === 'sales') return 'Sales Viewings Booked';
                                    if (t === 'lettings') return 'Lettings Viewings Booked';
                                    if (t === 'valuations') return 'Valuations Booked';
                                    if (
                                        t === 'fs bookings' ||
                                        t === 'fs_bookings' ||
                                        t === 'fsbookings'
                                    )
                                        return 'FS Bookings';
                                    return String(typeName)
                                        .split(/[\s-_]+/)
                                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                        .join(' ');
                                })();

                                return (
                                    <StyledCard
                                        key={String(typeName)}
                                        className="grid-xs-12 grid-s-6"
                                        title={cardTitle}>
                                        <Skeleton
                                            active
                                            loading={groupedByBrandLoading}
                                            paragraph={false}>
                                            <List
                                                dataSource={sorted}
                                                locale={{
                                                    emptyText: (
                                                        <span
                                                            style={{
                                                                color: 'var(--muted-color)',
                                                            }}>
                                                            No items
                                                        </span>
                                                    ),
                                                }}
                                                renderItem={(item) => (
                                                    <List.Item
                                                        key={String(item.label ?? Math.random())}
                                                        actions={[item.total ?? 0]}>
                                                        {item.label ?? '—'}
                                                    </List.Item>
                                                )}
                                            />
                                        </Skeleton>
                                    </StyledCard>
                                );
                            })}
                    </div>

                    <br />

                    <div className="grid">
                        <Statistic
                            title="Performance by Office"
                            styles={{ content: { display: 'none' } }}
                            className="grid-xs-12"
                        />

                        {Array.isArray(groupedByLocationData) &&
                            groupedByLocationData.map((group: any) => {
                                const typeName = group.type ?? 'Unknown';
                                const items: Array<{
                                    label?: string;
                                    total?: number;
                                }> = Array.isArray(group.items) ? group.items : [];

                                const sorted = items
                                    .slice()
                                    .sort((a, b) => (b.total ?? 0) - (a.total ?? 0));

                                const cardTitle = (() => {
                                    const t = String(typeName).toLowerCase();
                                    if (t === 'total') return 'Total interactions';
                                    if (t === 'sales') return 'Sales Viewings Booked';
                                    if (t === 'lettings') return 'Lettings Viewings Booked';
                                    if (t === 'valuations') return 'Valuations Booked';
                                    if (
                                        t === 'fs bookings' ||
                                        t === 'fs_bookings' ||
                                        t === 'fsbookings'
                                    )
                                        return 'FS Bookings';

                                    return String(typeName)
                                        .split(/[\s-_]+/)
                                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                        .join(' ');
                                })();

                                return (
                                    <StyledCard
                                        key={String(typeName)}
                                        className="grid-xs-12 grid-s-6"
                                        title={cardTitle}>
                                        <Skeleton
                                            active
                                            loading={groupedByLocationLoading}
                                            paragraph={false}>
                                            <List
                                                dataSource={sorted}
                                                locale={{
                                                    emptyText: (
                                                        <span
                                                            style={{
                                                                color: 'var(--muted-color)',
                                                            }}>
                                                            No items
                                                        </span>
                                                    ),
                                                }}
                                                renderItem={(item, index) => (
                                                    <List.Item
                                                        key={String(
                                                            item.label ?? `${typeName}-${index}`
                                                        )}
                                                        actions={[item.total ?? 0]}>
                                                        <StyledTag
                                                            style={{
                                                                marginRight: 10,
                                                            }}>
                                                            {index + 1}
                                                        </StyledTag>
                                                        {item.label ?? '—'}
                                                    </List.Item>
                                                )}
                                            />
                                        </Skeleton>
                                    </StyledCard>
                                );
                            })}
                    </div>
                </PageLayout.Content>
            </PageLayout>
        </>
    );
};

export default Dashboard;
